import React, { useEffect, useRef, useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { apiCall } from "../utils/apiService";
import { mediaUrl } from "../utils/wsUrl";
import "./SupportIssueChat.css";

/**
 * Live issue thread (text / file / voice).
 * role: "user" | "admin"
 */
const SupportIssueChat = ({
  role = "user",
  issue,
  onIssueUpdated,
  apiBase, // e.g. "support" or "support/admin"
}) => {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const fileRef = useRef(null);
  const endRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunks = useRef([]);
  const timerRef = useRef(null);
  const issueId = issue?.id;

  const loadMessages = async () => {
    if (!issueId) return;
    setLoading(true);
    try {
      const res = await apiCall({
        url: `${apiBase}/issues/${issueId}/messages`,
        method: "GET",
        role: role === "admin" ? "admin" : "user",
      });
      if (res?.success) {
        setMessages(res.data?.messages || []);
        if (res.data?.issue && onIssueUpdated) onIssueUpdated(res.data.issue);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMessages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [issueId]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  /** Append live WS message if it belongs to this issue */
  const appendLive = (msg) => {
    if (!msg?.id || Number(msg.issue_id) !== Number(issueId)) return;
    setMessages((prev) => {
      if (prev.some((m) => String(m.id) === String(msg.id))) return prev;
      return [...prev, msg];
    });
  };

  useEffect(() => {
    const handler = (e) => {
      const detail = e.detail;
      if (!detail) return;
      if (detail.type === "support_message" && detail.message) {
        appendLive(detail.message);
        if (detail.issue && onIssueUpdated) onIssueUpdated(detail.issue);
      }
    };
    window.addEventListener("support-ws", handler);
    return () => window.removeEventListener("support-ws", handler);
  }, [issueId, onIssueUpdated]);

  const sendPayload = async (body) => {
    setSending(true);
    try {
      const res = await apiCall({
        url: `${apiBase}/issues/${issueId}/messages`,
        method: "POST",
        body,
        role: role === "admin" ? "admin" : "user",
      });
      if (res?.success && res.data) {
        appendLive(res.data);
        setText("");
        setAudioBlob(null);
        if (fileRef.current) fileRef.current.value = "";
      }
      return res;
    } finally {
      setSending(false);
    }
  };

  const sendText = async (e) => {
    e?.preventDefault?.();
    if (!text.trim()) return;
    await sendPayload({ text: text.trim(), msg_type: "text" });
  };

  const sendFile = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    const mime = file.type || "";
    let msg_type = "document";
    if (mime.startsWith("image/")) msg_type = "image";
    else if (mime.startsWith("audio/")) msg_type = "audio";
    else if (mime.startsWith("video/")) msg_type = "video";
    fd.append("msg_type", msg_type);
    await sendPayload(fd);
  };

  const sendVoice = async () => {
    if (!audioBlob) return;
    const ext = (audioBlob.type || "").includes("ogg") ? "ogg" : "webm";
    const file = new File([audioBlob], `voice.${ext}`, {
      type: audioBlob.type || "audio/webm",
    });
    const fd = new FormData();
    fd.append("file", file);
    fd.append("msg_type", "audio");
    await sendPayload(fd);
  };

  const startRecording = () => {
    if (!navigator.mediaDevices?.getUserMedia) return;
    navigator.mediaDevices.getUserMedia({ audio: true }).then((stream) => {
      const preferred = ["audio/webm;codecs=opus", "audio/webm", "audio/ogg;codecs=opus"];
      const mimeType = preferred.find((t) => MediaRecorder.isTypeSupported(t)) || "";
      mediaRecorderRef.current = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream);
      audioChunks.current = [];
      mediaRecorderRef.current.ondataavailable = (e) => audioChunks.current.push(e.data);
      mediaRecorderRef.current.onstop = () => {
        const recordedType = mediaRecorderRef.current.mimeType || "audio/webm";
        setAudioBlob(new Blob(audioChunks.current, { type: recordedType }));
      };
      mediaRecorderRef.current.start();
      setIsRecording(true);
      setRecordingDuration(0);
      timerRef.current = setInterval(() => setRecordingDuration((d) => d + 1), 1000);
    });
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current.stream?.getTracks()?.forEach((t) => t.stop());
    }
    clearInterval(timerRef.current);
    setIsRecording(false);
  };

  const cancelRecording = () => {
    stopRecording();
    setAudioBlob(null);
    audioChunks.current = [];
  };

  const formatDuration = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  };

  const mine = (m) =>
    role === "admin" ? m.sender_role === "admin" : m.sender_role === "user";

  const renderBody = (m) => {
    const url = mediaUrl(m.text);
    if (m.msg_type === "image") {
      return (
        <a href={url} target="_blank" rel="noreferrer">
          <img src={url} alt="attachment" className="sic-media-img" />
        </a>
      );
    }
    if (m.msg_type === "audio") {
      return <audio controls src={url} className="sic-media-audio" />;
    }
    if (m.msg_type === "video") {
      return <video controls src={url} className="sic-media-video" />;
    }
    if (m.msg_type === "document") {
      return (
        <a href={url} target="_blank" rel="noreferrer" className="sic-doc-link">
          <Icon icon="mdi:file-document-outline" width={18} /> Open file
        </a>
      );
    }
    return <span className="sic-text">{m.text}</span>;
  };

  if (!issue) {
    return (
      <div className="sic-empty">
        <Icon icon="mdi:message-text-outline" width={40} />
        <p>Select an issue to open the chat.</p>
      </div>
    );
  }

  const closed = issue.status === "closed";

  return (
    <div className="sic-panel">
      <div className="sic-head">
        <div>
          <h6>{issue.subject}</h6>
          <span className={`sic-status status-${issue.status}`}>{issue.status}</span>
          {issue.user_name || issue.user_email ? (
            <span className="sic-meta">
              {issue.user_name}
              {issue.user_email ? ` · ${issue.user_email}` : ""}
            </span>
          ) : null}
        </div>
      </div>

      <div className="sic-messages">
        {loading ? (
          <p className="sic-hint">Loading messages…</p>
        ) : messages.length === 0 ? (
          <p className="sic-hint">No messages yet. Say hello.</p>
        ) : (
          messages.map((m) => (
            <div key={m.id} className={`sic-bubble ${mine(m) ? "mine" : "theirs"}`}>
              <div className="sic-bubble-body">{renderBody(m)}</div>
              <time>
                {m.created_at ? new Date(m.created_at).toLocaleString() : ""}
                {" · "}
                {m.sender_role === "admin" ? "Support" : "You"}
              </time>
            </div>
          ))
        )}
        <div ref={endRef} />
      </div>

      {closed && role === "user" ? (
        <div className="sic-closed">This issue is closed. Create a new issue to continue.</div>
      ) : (
        <div className="sic-composer">
          {audioBlob ? (
            <div className="sic-voice-preview">
              <audio controls src={URL.createObjectURL(audioBlob)} />
              <button type="button" className="btn btn-success btn-sm" disabled={sending} onClick={sendVoice}>
                Send voice
              </button>
              <button type="button" className="btn btn-light btn-sm" onClick={() => setAudioBlob(null)}>
                Discard
              </button>
            </div>
          ) : isRecording ? (
            <div className="sic-recording">
              <span className="sic-rec-dot" />
              Recording {formatDuration(recordingDuration)}
              <button type="button" className="btn btn-success btn-sm" onClick={stopRecording}>
                Stop
              </button>
              <button type="button" className="btn btn-light btn-sm" onClick={cancelRecording}>
                Cancel
              </button>
            </div>
          ) : (
            <form className="sic-form" onSubmit={sendText}>
              <button
                type="button"
                className="sic-icon-btn"
                title="Attach file"
                onClick={() => fileRef.current?.click()}
              >
                <Icon icon="mdi:paperclip" width={20} />
              </button>
              <input
                ref={fileRef}
                type="file"
                hidden
                accept="image/*,audio/*,video/*,.pdf,.doc,.docx,.zip"
                onChange={(e) => sendFile(e.target.files?.[0])}
              />
              <button
                type="button"
                className="sic-icon-btn"
                title="Voice message"
                onClick={startRecording}
              >
                <Icon icon="mdi:microphone" width={20} />
              </button>
              <input
                className="form-control"
                placeholder="Type a message…"
                value={text}
                onChange={(e) => setText(e.target.value)}
                disabled={sending}
              />
              <button type="submit" className="btn btn-success btn-sm" disabled={sending || !text.trim()}>
                Send
              </button>
            </form>
          )}
        </div>
      )}
    </div>
  );
};

export default SupportIssueChat;
